// Pages/aseos/components/NotasAyuda.tsx
//
// Icono (i) del panel de notas del aseo: explica, en lenguaje simple y para quien
// sube las fotos y videos (la aseadora en terreno, o el administrador), cómo
// funciona la subida. Cada punto está verificado contra el código
// (NotaEntradaArchivos, NotaColaSubida, colaSubidaStore, AvisoDatosMoviles,
// utils/imageCompression, utils/heic) y docs/rol-aseador.md §5.6.1.
import React from "react";
import InfoAyuda, { AyudaBloque } from "../../../Components/InfoAyuda";
import { MAX_VIDEO_SECONDS } from "../../../utils/videoDuration";
import { UMBRAL_AVISO_DATOS_BYTES } from "../../../utils/conexion";

const lista: React.CSSProperties = { margin: "4px 0 0", paddingLeft: 20 };

const MAX_MIN = MAX_VIDEO_SECONDS / 60;
const UMBRAL_MB = Math.round(UMBRAL_AVISO_DATOS_BYTES / (1024 * 1024));

/** `tactil`: en celular el botón mide 44 px para que el dedo acierte. */
const NotasAyuda: React.FC<{ tactil?: boolean }> = ({ tactil = false }) => (
  <InfoAyuda
    titulo="Cómo subir fotos y videos"
    texto="Cómo subir archivos"
    testId="ayuda-notas"
    style={tactil ? { minHeight: 44, minWidth: 44 } : undefined}
  >
    <AyudaBloque titulo="Cómo agregar fotos y videos">
      <ul style={lista}>
        <li>
          En el celular usa <strong>Tomar foto</strong> (abre la cámara), <strong>Grabar video</strong>{" "}
          (graba al momento) o <strong>Elegir de la galería o archivos</strong> (puedes marcar
          varios). En el computador arrastra los archivos, haz clic en la zona de subida o pega con
          Ctrl+V.
        </li>
        <li>
          También puedes subir documentos (PDF, Word, Excel, PowerPoint, texto o CSV).
        </li>
        <li>
          Un video puede durar hasta <strong>{MAX_MIN} minutos</strong> y cada archivo puede pesar
          hasta 3 GB. Si te pasas, el archivo se rechaza al elegirlo y no gastas datos.
        </li>
      </ul>
    </AyudaBloque>

    <AyudaBloque titulo="Cómo sube">
      Los archivos suben de a uno, con una barra que muestra cuánto va y cuánto falta. Los
      archivos grandes se envían por partes: si se corta la señal, la subida se pausa sola y sigue
      desde donde quedó cuando vuelve la conexión. Si cierras el panel, la subida continúa, pero
      <strong> no cierres ni bloquees el celular</strong> mientras sube: mantén la app abierta.
      Mientras tanto puedes seguir escribiendo el texto o agregando más archivos.
    </AyudaBloque>

    <AyudaBloque titulo="Qué significa cada estado">
      <ul style={lista}>
        <li>
          <strong>En espera:</strong> espera su turno.
        </li>
        <li>
          <strong>Optimizando…:</strong> se está reduciendo el peso de la foto antes de subirla.
        </li>
        <li>
          <strong>Subiendo:</strong> está enviándose.
        </li>
        <li>
          <strong>Reintentando:</strong> hubo un corte y lo intenta de nuevo solo.
        </li>
        <li>
          <strong>Sin conexión, en pausa:</strong> no hay internet; sigue sola al volver.
        </li>
        <li>
          <strong>Procesando en el servidor…:</strong> ya llegó entero y se está preparando.
        </li>
        <li>
          <strong>Listo:</strong> ya quedó en la nota.
        </li>
        <li>
          <strong>No se pudo subir / Cancelado:</strong> toca <strong>Reintentar</strong> (no hace
          falta volver a elegir el archivo) o <strong>Quitar</strong>. Si el servidor rechaza el
          archivo (por ejemplo, por su tipo), solo se puede quitar, porque reintentar no cambiaría
          nada.
        </li>
      </ul>
      <div style={{ marginTop: 6 }}>
        Mientras un archivo espera o sube puedes tocar <strong>Cancelar</strong>.
      </div>
    </AyudaBloque>

    <AyudaBloque titulo="Datos móviles">
      Si agregas archivos que suman más de {UMBRAL_MB} MB y el celular indica que usa datos
      móviles, tiene activado el ahorro de datos o la conexión es lenta, te preguntará antes de
      subirlos. Si puedes, conéctate a wifi. En algunos celulares (por ejemplo iPhone) no se puede
      saber qué conexión se está usando, y ahí no pregunta.
    </AyudaBloque>

    <AyudaBloque titulo="Fotos y videos que se ajustan solos">
      Las fotos se reducen automáticamente antes de subirlas, según la configuración del sistema,
      para gastar menos datos. Las fotos del iPhone en formato HEIC se convierten a JPG
      automáticamente; si alguna no se puede convertir, te pedirá volver a tomarla o guardarla como
      JPG. Los videos se suben tal cual los grabaste y el servidor puede optimizarlos después. Al verlos, el sistema usa una versión más liviana
      cuando existe, y puedes cambiar a HD con el botón sobre el video. Eso solo cambia lo que
      se descarga para verlo: el video se guarda siempre con la calidad con que se subió (o la que dejó la
      optimización), y la versión liviana es una copia aparte. En Almacenamiento y en el respaldo esa
      copia se cuenta junto al video (en el respaldo, como un archivo más).
    </AyudaBloque>

    <AyudaBloque titulo="El texto">
      Lo que escribes se guarda solo, poco después de que dejas de escribir (verás «Guardado»).
      Si falla el guardado o no hay señal, el texto queda guardado en tu celular y se envía de nuevo
      cuando se pueda; no se pierde al cerrar el panel.
    </AyudaBloque>
  </InfoAyuda>
);

export default NotasAyuda;
